import {
  Alert,
  Card,
  Heading,
  SkeletonBar,
  Text,
  vars,
} from "@janeapp/burrito-design-system"
import { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import styled, { css } from "styled-components"

/**
 * The cards want to switch layout once they are around 480px wide, which they
 * reach at a 528px viewport: the page column is `min(600px, viewport - 48px)`.
 *
 * This would ideally be a container query, matching the `container-type` Burrito
 * already sets on Card. It cannot be: styled-components 5 preprocesses with
 * stylis 0.8, which predates @container and emits the block with no selector,
 * so browsers discard it. Since the column width is derived from the viewport,
 * this media query is exactly equivalent for this layout.
 */
export const CARD_WIDE_BREAKPOINT = "528px"

/**
 * Titles are 20px/24px/0.2px on the desktop frame and 16px/20px/0.3px on
 * mobile. `level` only picks the element here, since both states override the
 * heading's own type; level 2 keeps the three cards sensible siblings.
 */
const CardTitle = styled(Heading)(
  () => css`
    font-size: ${vars.global.typography.fontSize.lg};
    line-height: ${vars.global.typography.lineHeight.md};
    letter-spacing: ${vars.global.typography.letterSpacing.lg};

    @media (min-width: ${CARD_WIDE_BREAKPOINT}) {
      font-size: ${vars.global.typography.fontSize.xl};
      line-height: ${vars.global.typography.lineHeight.lg};
      letter-spacing: ${vars.global.typography.letterSpacing.xl};
    }
  `
)

const Stack = styled.div<{ $gap: string }>(
  ({ $gap }) => css`
    display: flex;
    flex-direction: column;
    gap: ${$gap};
  `
)

/**
 * `Text` pairs its 16px size with a 24px line-height, but the design asks for
 * 20px. Those exact metrics belong to Heading level 4, which would be the wrong
 * element for a field label, so override instead.
 */
export const Label = styled(Text)(
  () => css`
    font-weight: ${vars.global.typography.weight.semibold};
    line-height: ${vars.global.typography.lineHeight.md};
  `
)

export const AccountCard = ({
  title,
  action,
  contentGap = vars.global.size["4x"],
  children,
}: {
  title: string
  action?: ReactNode
  contentGap?: string
  children: ReactNode
}) => (
  <Card>
    <Card.Content>
      <Stack $gap={vars.global.size["4x"]}>
        <div className="flex items-center justify-between gap-3">
          <CardTitle level={2}>{title}</CardTitle>
          {action}
        </div>
        <Stack $gap={contentGap}>{children}</Stack>
      </Stack>
    </Card.Content>
  </Card>
)

/**
 * A labelled value, optionally with an action aligned to its right. Renders a
 * skeleton in place of the value while the request that feeds it is in flight.
 */
export const Field = ({
  label,
  value,
  action,
  isLoading,
  placeholder,
}: {
  label: string
  value?: ReactNode
  action?: ReactNode
  isLoading?: boolean
  placeholder?: ReactNode
}) => {
  const hasValue = value !== null && value !== undefined && value !== ""

  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex min-w-0 flex-col gap-2">
        <Label elementType="span" size="lg">
          {label}
        </Label>
        {isLoading ? (
          <SkeletonBar size="md" hasRandomWidth />
        ) : hasValue ? (
          <Text elementType="span" size="md">
            {value}
          </Text>
        ) : placeholder ? (
          <Text elementType="span" size="md" color="subdued">
            {placeholder}
          </Text>
        ) : (
          <Text elementType="span" size="md">
            {value}
          </Text>
        )}
      </div>
      {action}
    </div>
  )
}

export const CardError = () => {
  const { t } = useTranslation()
  return (
    <Alert tone="critical">
      <Alert.Content>{t("error.oopsSomethingWentWrong")}</Alert.Content>
    </Alert>
  )
}
